import type { ReactNode } from 'react';
import { css } from '@emotion/react';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Text, Button } from '@frontend/design-system';

type TaskProps = {
  completed?: boolean;
  disabled?: boolean;
  loading?: boolean;
  onSelect?: () => void;
  description: string;
  label: string;
  trackingId?: string;
};

function OnboardingChecklist({ children }: { children: ReactNode }) {
  return <div css={styles.rootWrapper}>{children}</div>;
}

function Header({ children, onClose, title }: { children?: ReactNode; onClose: () => void; title: string }) {
  return (
    <div css={styles.header}>
      <div css={styles.headerRow}>
        <Text weight='bold'>{title}</Text>
        <Button iconName='x' variant='secondary' onClick={onClose} css={styles.closeButton} tabIndex={-1} />
      </div>
      {children}
    </div>
  );
}

function Body({ children }: { children: ReactNode }) {
  return <div css={styles.body}>{children}</div>;
}

function TaskList({ children }: { children: ReactNode }) {
  return (
    <ul css={styles.taskList} role='list'>
      {children}
    </ul>
  );
}

function Task({
  completed = false,
  disabled = false,
  loading = false,
  onSelect,
  description,
  label,
  trackingId,
}: TaskProps) {
  const isDisabled = disabled || loading;

  const handleClick = () => {
    if (isDisabled) return;
    onSelect?.();
  };

  return (
    <li css={styles.task}>
      <span aria-hidden css={styles.taskCheckbox(completed)}>
        {completed && <Icon name='check-small' css={styles.taskCheckboxIcon} />}
      </span>
      <div css={styles.taskBody}>
        <Text weight='bold' size='medium' css={styles.taskLabel(completed)}>
          {label}
        </Text>
        <Text size='small' css={styles.taskDescription(completed)}>
          {description}
        </Text>
      </div>
      {!!onSelect && (
        <Button
          iconName='arrow-right-small'
          variant='secondary'
          size='small'
          disabled={isDisabled}
          css={styles.taskButton}
          trackingId={trackingId}
          onClick={handleClick}
        />
      )}
    </li>
  );
}

OnboardingChecklist.Header = Header;
OnboardingChecklist.Body = Body;
OnboardingChecklist.TaskList = TaskList;
OnboardingChecklist.Task = Task;

export default OnboardingChecklist;

const styles = {
  rootWrapper: css`
    display: flex;
    flex-direction: column;
    width: 315px;
  `,
  header: css`
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(1.5)};
    background: ${theme.colorsV2.seaweed50(10)};
    border-bottom: 1px solid ${theme.colors.secondary.seaweed50};
  `,
  headerRow: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spacing(1)};
  `,
  closeButton: css`
    width: ${theme.spacing(3)};
    height: ${theme.spacing(3)};
    padding: 0;
  `,
  body: css`
    padding: ${theme.spacing(1.5)};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1.5)};
  `,
  taskList: css`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1.5)};
  `,
  task: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,

  taskCheckbox: (completed: boolean) => css`
    width: ${theme.spacing(2.5)};
    height: ${theme.spacing(2.5)};
    border-radius: ${theme.borderRadius.small};
    border: 2px solid ${completed ? theme.colors.secondary.seaweed50 : theme.colorsV2.black(35)};
    background: ${theme.colors.white};
    color: ${theme.colors.secondary.seaweed50};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  `,

  taskBody: css`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.25)};
  `,

  taskButton: css`
    width: ${theme.spacing(3)};
    height: ${theme.spacing(3)};
  `,
  taskCheckboxIcon: css`
    width: ${theme.spacing(3.75)};
    height: ${theme.spacing(3.75)};
    min-width: ${theme.spacing(3.75)};
    min-height: ${theme.spacing(3.75)};
    color: #0d5d59;
  `,
  taskLabel: (isTaskCompleted: boolean) =>
    css`
      color: ${!isTaskCompleted ? theme.colorsV2.black(85) : theme.colorsV2.black(60)};
    `,
  taskDescription: (isTaskCompleted: boolean) =>
    css`
      color: ${!isTaskCompleted ? theme.colorsV2.black(75) : theme.colorsV2.black(60)};
    `,
};
